import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    SparklesIcon,
    EnvelopeIcon,
    ExclamationCircleIcon,
    CheckCircleIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

const PasswordResetRequest = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Regex pour la validation de l'email
    const regexEmail = /^[^@\s]{2,30}@[^@\s]{2,30}\.[^@\s]{2,5}$/;

    const validateEmail = (email) => {
        if (!regexEmail.test(email)) {
            setEmailError("L'adresse email n'est pas valide.");
        } else {
            setEmailError('');
        }
    };

    const onChange = e => {
        setEmail(e.target.value);
        setMessage('');
        setError('');
        
        // Supprimer les erreurs quand l'utilisateur tape
        if (emailError) {
            setEmailError('');
        }
    };

    const onBlur = e => {
        // Valider seulement quand l'utilisateur quitte le champ
        if (e.target.value) {
            validateEmail(e.target.value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Valider l'email avant d'envoyer la requête
        if (!regexEmail.test(email)) {
            setEmailError("L'adresse email n'est pas valide.");
            return;
        }

        setIsSubmitting(true);
        setMessage('');
        setError('');

        try {
            const response = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/password/forgot-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            if (response.ok) {
                setMessage('Email de réinitialisation envoyé avec succès ! Vérifiez votre boîte de réception.');
                setError('');
            } else {
                setError(data.error || 'Une erreur est survenue lors de l\'envoi de l\'email.');
            }
        } catch (err) {
            setError('Erreur de connexion. Veuillez réessayer.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page-shell flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                            <SparklesIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Mot de passe oublié
                        </h1>
                    </div>
                    <p className="text-gray-600">
                        Entrez votre adresse email pour recevoir un lien de réinitialisation
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <EnvelopeIcon className="w-4 h-4 mr-2 text-blue-600" />
                            Adresse email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={onChange}
                            onBlur={onBlur}
                            required
                            className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white ${
                                emailError ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="exemple@email.com"
                        />
                        {emailError && (
                            <div className="mt-2 flex items-center text-red-500 text-sm">
                                <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                                <span>{emailError}</span>
                            </div>
                        )}
                    </div>

                    {/* Bouton de soumission */}
                    <button
                        type="submit"
                        disabled={isSubmitting || emailError}
                        className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                            isSubmitting || emailError
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                Envoi en cours...
                            </>
                        ) : (
                            <>
                                <EnvelopeIcon className="w-5 h-5 mr-2" />
                                Envoyer le lien
                            </>
                        )}
                    </button>

                    {/* Messages */}
                    {message && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center">
                            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-green-600 text-sm">{message}</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
                            <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                            <span className="text-red-600 text-sm">{error}</span>
                        </div>
                    )}

                    {/* Lien de retour */}
                    <div className="text-center pt-4 border-t border-gray-200">
                        <Link 
                            to="/login" 
                            className="text-blue-600 hover:text-blue-700 font-semibold transition-colors flex items-center justify-center"
                        >
                            <ArrowLeftIcon className="w-4 h-4 mr-2" />
                            Retour à la connexion
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PasswordResetRequest;
